import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  LOCALE_ID,
  OnInit,
  signal,
} from '@angular/core';
import { Router } from '@angular/router';
import { finalize } from 'rxjs';
import { CategoryService } from '../../../../services/category/category.service';
import { CategoryListItem } from '../../../../pages/admin-dashboard/categories/category.model';
import { Pagination } from '../../../../shared/ui/shared-tables/pagination/pagination';
import {
  buildPageNumbers,
  getEndItem,
  getStartItem,
  LoadState,
} from '../../../../shared/ui/shared-tables/pagination/pagination.utils';
import { ConfirmationDialog } from '../../../../shared/ui/confirmation-dialog/confirmation-dialog';
import { SuccessfulNotification } from '../../../../utils/successful-notification/successful-notification';
import { CategoryFiltersComponent } from './category-filters-component/category-filters-component';
import { CategoryFilters, DEFAULT_CATEGORY_FILTERS } from './category-filters-component/category-filters-component.model';
import { CategoryTableComponent } from './category-table-component/category-table-component';

@Component({
  selector: 'app-admin-categories-list',
  imports: [CategoryFiltersComponent, CategoryTableComponent, Pagination, ConfirmationDialog, SuccessfulNotification],
  templateUrl: './admin-categories-list.html',
  styleUrl: './admin-categories-list.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminCategoriesList implements OnInit {
  private router = inject(Router);
  private categoryService = inject(CategoryService);
  private locale = inject(LOCALE_ID);
  private destroyRef = inject(DestroyRef);

  isArabic = computed(() => this.locale.startsWith('ar'));

  filters = signal<CategoryFilters>({ ...DEFAULT_CATEGORY_FILTERS });
  categories = signal<CategoryListItem[]>([]);
  state = signal<LoadState>('loading');

  currentPage = signal(1);
  itemsPerPage = signal(25);

  totalItems = signal(0);
  totalPages = computed(() => Math.max(1, Math.ceil(this.totalItems() / this.itemsPerPage())));
  startItem = computed(() => getStartItem(this.currentPage(), this.itemsPerPage(), this.totalItems()));
  endItem = computed(() => getEndItem(this.currentPage(), this.itemsPerPage(), this.totalItems()));
  pageNumbers = computed(() => buildPageNumbers(this.currentPage(), this.totalPages()));
  isFiltersApplied = computed(() => {
    const f = this.filters();
    return !!(f.categoryTitle || f.createdOnFrom || f.createdOnTo || f.lastUpdatedOnFrom || f.lastUpdatedOnTo);
  });

  categoryToDelete = signal<CategoryListItem | null>(null);
  deleting = signal(false);
  deleteError = signal('');
  showSuccessMessage = signal(false);

  deleteTitle = $localize`:@@adminCategoriesDeleteTitle:Delete Category?`;
  deleteCancel = $localize`:@@adminProductsDeleteCancel:Cancel`;
  deleteConfirm = $localize`:@@adminProductsDeleteConfirmButton:Delete`;
  deleteSuccess = $localize`:@@adminCategoriesDeleteSuccess:Category deleted successfully!`;
  deleteDescription = computed(() => {
    const category = this.categoryToDelete();
    const name = (this.isArabic() ? category?.titleAr : category?.titleEn) || category?.titleEn || '';
    return `${$localize`:@@adminProductsDeleteConfirm:Are you sure you want to delete`} ${name}?`;
  });

  ngOnInit() {
    this.loadCategories();
  }

  loadCategories() {
    this.state.set('loading');
    const filters = this.filters();

    const subscription = this.categoryService
      .getAllCategories({
        categoryTitle: filters.categoryTitle || undefined,
        pageNum: this.currentPage() - 1,
        pageSize: this.itemsPerPage(),
        orderBy: filters.orderBy,
        orderDir: filters.orderDir,
      })
      .subscribe({
        next: (res) => {
          this.categories.set(this.filterByDates(res.list ?? [], filters));
          this.totalItems.set(res.total);
          this.state.set('success');
        },
        error: () => this.state.set('error'),
      });
    this.destroyRef.onDestroy(() => subscription.unsubscribe());
  }

  applyFilters(filters: CategoryFilters) {
    this.filters.set(filters);
    this.currentPage.set(1);
    this.loadCategories();
  }

  resetFilters() {
    this.filters.set({ ...DEFAULT_CATEGORY_FILTERS });
    this.currentPage.set(1);
    this.loadCategories();
  }

  onPageChange(page: number) {
    if (page >= 1 && page <= this.totalPages()) {
      this.currentPage.set(page);
      this.loadCategories();
    }
  }

  onPageSizeChange(size: number) {
    this.itemsPerPage.set(size);
    this.currentPage.set(1);
    this.loadCategories();
  }

  onViewCategory(id: number) {
    this.router.navigate(['/admin/categories/view-category', id]);
  }

  onEditCategory(id: number) {
    this.router.navigate(['/admin/categories/edit-category', id]);
  }

  openDeleteDialog(id: number) {
    this.deleteError.set('');
    this.categoryToDelete.set(this.categories().find((c) => c.id === id) ?? null);
  }

  closeDeleteDialog() {
    this.categoryToDelete.set(null);
  }

  confirmDelete() {
    const category = this.categoryToDelete();
    if (!category) return;

    this.deleting.set(true);
    this.deleteError.set('');
    const subscription = this.categoryService
      .deleteCategory(category.id)
      .pipe(finalize(() => this.deleting.set(false)))
      .subscribe({
        next: () => {
          this.closeDeleteDialog();
          this.showSuccessMessage.set(true);
          setTimeout(() => this.showSuccessMessage.set(false), 3000);
          if (this.categories().length === 1 && this.currentPage() > 1) {
            this.currentPage.update((page) => page - 1);
          }
          this.loadCategories();
        },
        error: (error) =>
          this.deleteError.set(
            this.isArabic()
              ? error?.messageAr || 'حدث بعض الخطأ'
              : error?.messageEn || 'Error deleting category. Please try again later.',
          ),
      });
    this.destroyRef.onDestroy(() => subscription.unsubscribe());
  }

  /** The API has no date filters for categories, so they are applied to the loaded page. */
  private filterByDates(list: CategoryListItem[], f: CategoryFilters): CategoryListItem[] {
    const startOf = (value: string) => new Date(value);
    const endOf = (value: string) => {
      const date = new Date(value);
      date.setHours(23, 59, 59, 999);
      return date;
    };

    // Categories carry only a creation date, which also stands for "last updated".
    return list.filter((c) => {
      if (!c.createdOn) return !(f.createdOnFrom || f.createdOnTo || f.lastUpdatedOnFrom || f.lastUpdatedOnTo);
      const created = new Date(c.createdOn);
      if (f.createdOnFrom && created < startOf(f.createdOnFrom)) return false;
      if (f.createdOnTo && created > endOf(f.createdOnTo)) return false;
      if (f.lastUpdatedOnFrom && created < startOf(f.lastUpdatedOnFrom)) return false;
      if (f.lastUpdatedOnTo && created > endOf(f.lastUpdatedOnTo)) return false;
      return true;
    });
  }
}
